import { safeLocalStorage } from "../../utils/safeStorage";

const cleanString = (value = "", maxLength = 120) =>
  String(value ?? "")
    .trim()
    .slice(0, maxLength);

const cleanKey = (value = "", maxLength = 80) =>
  cleanString(value, maxLength)
    .replace(/[^a-zA-Z0-9_./:-]+/g, "_")
    .replace(/_+/g, "_")
    .replace(/^_+|_+$/g, "");

const cleanLocationLabel = (value = "") =>
  cleanString(value, 80)
    .replace(/[^\p{L}\p{N}\s.'-]+/gu, "")
    .trim();

const stateCodeFrom = (...values) => {
  for (const value of values) {
    const code = cleanString(value, 8)
      .toUpperCase()
      .replace(/[^A-Z]/g, "");
    if (/^[A-Z]{2}$/.test(code)) return code;
  }
  return "";
};

const viewportBucketFrom = (screenSize = "") => {
  const [widthRaw] = String(screenSize || "").split("x");
  if (!String(widthRaw || "").trim()) return "unknown";
  const width = Number(widthRaw);
  if (!Number.isFinite(width)) return "unknown";
  if (width < 600) return "mobile";
  if (width < 960) return "tablet";
  return "desktop";
};

const browserFamilyFrom = (browserValue = "") => {
  const browser = cleanString(browserValue, 80).toLowerCase();
  if (browser.includes("edge")) return "edge";
  if (browser.includes("chrome")) return "chrome";
  if (browser.includes("safari")) return "safari";
  if (browser.includes("firefox")) return "firefox";
  return "unknown";
};

const EXTRA_CONTEXT_KEYS = new Set([
  "actionVariantKey",
  "contentVariantKey",
  "ctaPositionVariantKey",
  "desktopCtaPlacementKey",
  "desktopHeroMediaVariantKey",
  "deviceCategory",
  "flow",
  "founderPlacementKey",
  "heroMediaFrameKey",
  "hasDisplayLine",
  "inventoryVersion",
  "matchType",
  "mediaVariantKey",
  "mobileCtaPlacementKey",
  "mobileHeroMediaLayoutVariantKey",
  "mobileHeroMediaOrderKey",
  "numberType",
  "route",
  "routePath",
  "source",
  "stateCode",
  "surface",
  "triggerSource",
  "trustSignalVariantKey",
  "videoId",
  "viewportBucket",
]);

const addBoundedExtraContext = (context, extraContext = {}) => {
  if (
    !extraContext ||
    typeof extraContext !== "object" ||
    Array.isArray(extraContext)
  ) {
    return context;
  }
  Object.entries(extraContext).forEach(([key, value]) => {
    if (!EXTRA_CONTEXT_KEYS.has(key)) return;
    if (value === null || value === undefined || value === "") return;
    if (!["string", "number", "boolean"].includes(typeof value)) return;
    const normalized =
      typeof value === "boolean" ? value : cleanKey(value, 120);
    if (normalized !== "") context[key] = normalized;
  });
  return context;
};

export const readExperimentStorageValue = (key = "") => {
  try {
    return safeLocalStorage.getItem(key);
  } catch (_error) {
    return null;
  }
};

export const buildExperimentEventContext = ({
  utmSource = null,
  systemData = null,
  locationData = null,
  extraContext = {},
} = {}) => {
  const context = {};
  const resolvedUtmSource = cleanKey(
    utmSource || readExperimentStorageValue("utm_source") || "",
    80,
  );
  if (resolvedUtmSource) context.utmSource = resolvedUtmSource;

  if (systemData && typeof systemData === "object") {
    context.deviceCategory =
      cleanKey(systemData.deviceCategory || "unknown", 40) || "unknown";
    context.browserFamily = browserFamilyFrom(systemData.browser);
    context.viewportBucket = viewportBucketFrom(systemData.screenSize);
  }

  if (locationData && typeof locationData === "object") {
    const stateCode = stateCodeFrom(
      locationData.stateCode,
      locationData.state_code,
      locationData.region_code,
      locationData.state,
      locationData.region,
    );
    if (stateCode) context.stateCode = stateCode;

    const city = cleanLocationLabel(
      locationData.city || locationData.locality || "",
    );
    if (city) context.city = city;

    const state = cleanLocationLabel(
      locationData.region || locationData.state || "",
    );
    if (state) context.state = state;

    const locationSource = cleanKey(
      locationData.locationSource || locationData.source || "",
      80,
    );
    if (locationSource) context.locationSource = locationSource;

    const routePath = cleanKey(locationData.routePath || "", 120);
    if (routePath) context.routePath = routePath;
  }

  return addBoundedExtraContext(context, extraContext);
};

const normalizeExperimentContexts = (contexts = []) => {
  if (Array.isArray(contexts)) return contexts;
  if (contexts && typeof contexts === "object") return Object.values(contexts);
  return [];
};

export const buildExperimentFunnelProperties = (contexts = []) => {
  const seen = new Set();
  const entries = normalizeExperimentContexts(contexts)
    .map((context) => ({
      experimentKey: String(context?.experimentKey || "").trim(),
      variantKey: String(context?.variantKey || "").trim(),
    }))
    .filter((context) => {
      if (!context.experimentKey || !context.variantKey) return false;
      const pairKey = `${context.experimentKey}:${context.variantKey}`;
      if (seen.has(pairKey)) return false;
      seen.add(pairKey);
      return true;
    });

  if (!entries.length) return {};

  return {
    experimentKeys: entries.map((entry) => entry.experimentKey),
    variantKeys: entries.map((entry) => entry.variantKey),
    experimentPairs: entries.map(
      (entry) => `${entry.experimentKey}:${entry.variantKey}`,
    ),
  };
};

export const mergeExperimentFunnelProperties = (...propertySets) => {
  const contexts = propertySets.flatMap((properties) => {
    if (!properties || typeof properties !== "object") return [];
    const pairs = Array.isArray(properties.experimentPairs)
      ? properties.experimentPairs
      : [];
    if (pairs.length) {
      return pairs.map((pair) => {
        const normalizedPair = String(pair || "").trim();
        const separatorIndex = normalizedPair.indexOf(":");
        if (separatorIndex <= 0) return null;
        return {
          experimentKey: normalizedPair.slice(0, separatorIndex),
          variantKey: normalizedPair.slice(separatorIndex + 1),
        };
      });
    }
    const experimentKeys = Array.isArray(properties.experimentKeys)
      ? properties.experimentKeys
      : [];
    const variantKeys = Array.isArray(properties.variantKeys)
      ? properties.variantKeys
      : [];
    return experimentKeys.map((experimentKey, index) => ({
      experimentKey,
      variantKey: variantKeys[index],
    }));
  });

  return buildExperimentFunnelProperties(contexts.filter(Boolean));
};
